import { AcceptanceStaleReason, AzureRowKind } from './acceptance/acceptances';
import { TFunction } from 'i18next';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import {
  fromApiDateOptional,
  fromApiDateTimeOptional,
  fromApiDateTimeRequired,
  toLocaleDateShort,
  toLocaleDateTime,
} from 'utils/datetime';
import {
  AzureDatabaseDetailDTO,
  AzureEvidenceDTO,
  AzureLogDetailDTO,
  AzureRowDTO,
  AzureSecretDetailDTO,
  AzureStaleAcceptanceDTO,
  AzureSubscriptionRowDTO,
  AzureVaultDetailDTO,
} from './AzureDTO';
import {
  AzureCheck,
  AzureCounts,
  AzureDatabaseDetail,
  AzureFirewallRule,
  AzureLogDetail,
  AzureRow,
  AzureSecretDetail,
  AzureStaleAcceptance,
  AzureSubscriptionRow,
  AzureUsage,
  AzureVaultDetail,
  getAzureRowName,
  getAzureSubscriptionName,
  IAzureResult,
  mapFromAzureAcceptance,
} from './AzureModels';
import {
  getAzureMetaLines,
  getAzureOutcomeText,
  getAzureResourceTypeText,
  getAzureStateDetail,
  getAzureUsageDescription,
  getAzureUsageFacts,
  getAzureUsageMessage,
} from './AzureTexts';

const getAzureCheckName = (check: AzureCheck, t: TFunction<string[]>): string => {
  switch (check) {
    case AzureCheck.Databases:
      return t('integrations:Checks.DatabasesNotPublic.Name');
    case AzureCheck.KeyVaults:
      return t('integrations:Checks.KeyVaultsLockedDown.Name');
    case AzureCheck.Logs:
      return t('integrations:Checks.LogsKept.Name');
    default:
      return t('integrations:Checks.SecretsExpiry.Name');
  }
};

//
// Evidence: one shape for the four Azure checks. The rows are the resources that do not meet the rule (capped at
// 100); the counts are complete.
//
export class AzureEvidence implements IIntegrationEvidence, IAzureResult {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  check: AzureCheck = AzureCheck.Databases;

  usage: AzureUsage = AzureUsage.InUse;

  pass: boolean = false;

  azureTenantId: string = '';

  minimumRetentionDays?: number;

  warnDays?: number;

  allowAzureServicesIsFinding: boolean = false;

  counts: AzureCounts = new AzureCounts();

  subscriptions: AzureSubscriptionRow[] = [];

  rows: AzureRow[] = [];

  noLongerApplies: AzureStaleAcceptance[] = [];

  toMarkdownReport(t: TFunction<string[]>): string {
    return getAzureMarkdownReport(this, t);
  }
}

//
// Mapping from the API's evidence JSON (AzureDTO): a DateOnly is read with fromApiDateOptional, a DateTimeOffset with
// fromApiDateTime...; null becomes undefined or the model's default
//
const mapFromAzureSubscriptionRow = (dto: AzureSubscriptionRowDTO): AzureSubscriptionRow => {
  const subscription = new AzureSubscriptionRow();
  subscription.subscriptionId = dto.subscriptionId ?? '';
  subscription.displayName = dto.displayName ?? '';
  subscription.scopeState = dto.scopeState;
  subscription.readState = dto.readState;
  subscription.resources = dto.resources;
  subscription.excludedResourceGroups = dto.excludedResourceGroups;

  return subscription;
};

const mapFromAzureDatabaseDetail = (dto: AzureDatabaseDetailDTO): AzureDatabaseDetail => {
  const detail = new AzureDatabaseDetail();
  detail.publicNetworkAccess = dto.publicNetworkAccess ?? undefined;
  detail.entraOnlyAuth = dto.entraOnlyAuth ?? undefined;
  detail.ruleCount = dto.ruleCount;
  detail.privateEndpoints = dto.privateEndpoints;
  detail.allowAzureServices = dto.allowAzureServices;
  detail.rules = dto.rules.map((ruleDto) => {
    const rule = new AzureFirewallRule();
    rule.name = ruleDto.name ?? '';
    rule.startIp = ruleDto.startIp ?? '';
    rule.endIp = ruleDto.endIp ?? '';
    rule.addresses = ruleDto.addresses;

    return rule;
  });

  return detail;
};

const mapFromAzureVaultDetail = (dto: AzureVaultDetailDTO): AzureVaultDetail => {
  const detail = new AzureVaultDetail();
  detail.rbacAuthorization = dto.rbacAuthorization ?? undefined;
  detail.purgeProtection = dto.purgeProtection ?? undefined;
  detail.softDelete = dto.softDelete ?? undefined;
  detail.softDeleteRetentionDays = dto.softDeleteRetentionDays ?? undefined;
  detail.publicNetworkAccess = dto.publicNetworkAccess ?? undefined;

  return detail;
};

const mapFromAzureLogDetail = (dto: AzureLogDetailDTO): AzureLogDetail => {
  const detail = new AzureLogDetail();
  detail.source = dto.source;
  detail.destination = dto.destination;
  detail.enabled = dto.enabled;
  detail.retentionDays = dto.retentionDays ?? undefined;
  detail.retentionSource = dto.retentionSource;
  detail.categories = dto.categories;
  detail.workspaceId = dto.workspaceId ?? undefined;

  return detail;
};

const mapFromAzureSecretDetail = (dto: AzureSecretDetailDTO): AzureSecretDetail => {
  const detail = new AzureSecretDetail();
  detail.itemType = dto.itemType;
  detail.vaultName = dto.vaultName ?? '';
  detail.enabled = dto.enabled;
  detail.expiresUtc = fromApiDateTimeOptional(dto.expiresUtc ?? undefined);
  detail.createdUtc = fromApiDateTimeOptional(dto.createdUtc ?? undefined);
  detail.daysLeft = dto.daysLeft ?? undefined;

  return detail;
};

const mapFromAzureRow = (dto: AzureRowDTO): AzureRow => {
  const row = new AzureRow();
  row.resourceId = dto.resourceId ?? '';
  row.resourceName = dto.resourceName ?? '';
  row.resourceGroup = dto.resourceGroup ?? '';
  row.subscriptionId = dto.subscriptionId ?? '';
  row.resourceType = dto.resourceType;
  row.state = dto.state;
  row.kind = dto.kind;
  row.found = dto.found;
  row.expected = dto.expected;
  row.acceptedUntil = fromApiDateOptional(dto.acceptedUntil ?? undefined);
  row.acceptedReason = dto.acceptedReason ?? undefined;
  row.acceptedBy = dto.acceptedBy ?? undefined;
  row.acceptedAtUtc = fromApiDateTimeOptional(dto.acceptedAtUtc ?? undefined);
  row.database = dto.database ? mapFromAzureDatabaseDetail(dto.database) : undefined;
  row.vault = dto.vault ? mapFromAzureVaultDetail(dto.vault) : undefined;
  row.log = dto.log ? mapFromAzureLogDetail(dto.log) : undefined;
  row.secret = dto.secret ? mapFromAzureSecretDetail(dto.secret) : undefined;

  return row;
};

const mapFromAzureStaleAcceptance = (dto: AzureStaleAcceptanceDTO): AzureStaleAcceptance => {
  const stale = new AzureStaleAcceptance();
  stale.acceptance = mapFromAzureAcceptance(dto.acceptance);
  stale.why = dto.why;

  return stale;
};

const mapFromAzureEvidence = (dto: AzureEvidenceDTO): AzureEvidence => {
  const evidence = new AzureEvidence();
  evidence.isCapped = dto.isCapped;
  evidence.evaluatedAtUtc = fromApiDateTimeRequired(dto.evaluatedAtUtc);
  evidence.check = dto.check;
  evidence.usage = dto.usage;
  evidence.pass = dto.pass;
  evidence.azureTenantId = dto.azureTenantId ?? '';
  evidence.minimumRetentionDays = dto.minimumRetentionDays ?? undefined;
  evidence.warnDays = dto.warnDays ?? undefined;
  evidence.allowAzureServicesIsFinding = dto.allowAzureServicesIsFinding;
  evidence.counts = Object.assign(new AzureCounts(), dto.counts);
  evidence.subscriptions = dto.subscriptions.map(mapFromAzureSubscriptionRow);
  evidence.rows = dto.rows.map(mapFromAzureRow);
  evidence.noLongerApplies = dto.noLongerApplies.map(mapFromAzureStaleAcceptance);

  return evidence;
};

export const parse_Evidence_MS_Azure = (raw: string | undefined): AzureEvidence | undefined => {
  if (!raw) return undefined;
  const dto: AzureEvidenceDTO = JSON.parse(raw);

  return mapFromAzureEvidence(dto);
};

//
// Evidence report (markdown)
//
const getRowLine = (row: AzureRow, evidence: AzureEvidence, t: TFunction<string[]>): string => {
  return t('integrations:MS_Azure.Report.RowLine', {
    name: getAzureRowName(row),
    type: getAzureResourceTypeText(row.resourceType, t),
    subscription: getAzureSubscriptionName(evidence, row.subscriptionId),
    state: getAzureStateDetail(row, evidence, t),
  });
};

const getAzureMarkdownReport = (evidence: AzureEvidence, t: TFunction<string[]>): string => {
  const sb: string[] = [];
  sb.push(
    `## ${t('integrations:MS_Azure.Report.Heading', {
      check: getAzureCheckName(evidence.check, t),
      date: toLocaleDateTime(evidence.evaluatedAtUtc),
    })}`,
  );

  if (evidence.usage !== AzureUsage.InUse) {
    sb.push(getAzureUsageMessage(evidence, t));
    sb.push(getAzureUsageDescription(evidence, t));
    getAzureUsageFacts(evidence, t).forEach((fact) => sb.push(`- ${fact.label}: ${fact.value}`));

    return sb.join('\n');
  }

  sb.push(`- ${t('integrations:MS_Azure.Report.Outcome', { outcome: getAzureOutcomeText(evidence, t) })}`);
  getAzureMetaLines(evidence, t).forEach((line) => sb.push(`- ${line}`));
  //the resource groups outside the ISMS scope, per subscription: what was not checked, for the auditor
  evidence.subscriptions
    .filter((subscription) => subscription.excludedResourceGroups.length > 0)
    .forEach((subscription) =>
      sb.push(
        `- ${t('integrations:MS_Azure.Report.ExcludedResourceGroups', {
          subscription: subscription.displayName || subscription.subscriptionId,
          names: subscription.excludedResourceGroups.join(', '),
        })}`,
      ),
    );
  sb.push(
    `- ${t('integrations:MS_Azure.Report.Counts', {
      resources: evidence.counts.resources,
      ok: evidence.counts.ok,
      findings: evidence.counts.findings,
      accepted: evidence.counts.accepted,
      explained: evidence.counts.explained,
      notCounted: evidence.counts.notCounted,
    })}`,
  );

  const section = (heading: string, kind: AzureRowKind) => {
    const rows = evidence.rows.filter((row) => row.kind === kind);
    if (rows.length === 0) return;
    sb.push('');
    sb.push(`### ${heading}`);
    rows.forEach((row) => {
      sb.push(`- ${getRowLine(row, evidence, t)}`);
      if (kind === AzureRowKind.Accepted) {
        const values = {
          until: toLocaleDateShort(row.acceptedUntil),
          user: row.acceptedBy,
          date: toLocaleDateShort(row.acceptedAtUtc),
          reason: row.acceptedReason ?? '',
        };
        sb.push(
          `  ${
            row.acceptedBy
              ? t('integrations:MS_Azure.Report.AcceptedLine', values)
              : t('integrations:MS_Azure.Report.AcceptedLineNoUser', values)
          }`,
        );
      }
    });
  };

  section(t('integrations:MS_Azure.Report.FindingsHeading'), AzureRowKind.Finding);
  section(t('integrations:MS_Azure.Report.AcceptedHeading'), AzureRowKind.Accepted);
  section(t('integrations:MS_Azure.Report.ExplainedHeading'), AzureRowKind.Explained);
  section(t('integrations:MS_Azure.Report.NotCountedHeading'), AzureRowKind.NotCounted);

  if (evidence.noLongerApplies.length > 0) {
    sb.push('');
    sb.push(`### ${t('integrations:MS_Azure.Report.NoLongerAppliesHeading')}`);
    evidence.noLongerApplies.forEach((stale) => {
      const name = stale.acceptance.resourceName || stale.acceptance.resourceId;
      sb.push(
        `- ${
          stale.why === AcceptanceStaleReason.NotFound
            ? t('integrations:MS_Azure.Report.StaleNotFound', { name })
            : t('integrations:MS_Azure.Report.StaleExpired', {
                name,
                until: toLocaleDateShort(fromApiDateOptional(stale.acceptance.until)),
              })
        }`,
      );
    });
  }

  return sb.join('\n');
};
